"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableColumn = void 0;
var tslib_1 = require("tslib");
/* eslint-disable @typescript-eslint/naming-convention */
var react_1 = tslib_1.__importStar(require("react"));
var react_table_1 = require("react-table");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var difference_1 = tslib_1.__importDefault(require("lodash/difference"));
var ConfigProvider_1 = require("../ConfigProvider");
var Pagination_1 = tslib_1.__importDefault(require("./Pagination"));
var utils_1 = require("../utils");
var column_options_1 = require("./column-options");
var hooks_1 = require("./hooks");
var Placeholder_1 = tslib_1.__importDefault(require("./Placeholder"));
var Loading_1 = require("../Loading");
var withSelectionColumn_1 = tslib_1.__importDefault(require("./plugins/withSelectionColumn"));
var utils_2 = require("./utils");
var hooks_2 = require("../utils/hooks");
function Table(props) {
    var _a;
    var records = props.records, _b = props.pagination, pagination = _b === void 0 ? false : _b, recordIdAccessor = props.recordIdAccessor, tableHtmlProps = props.tableHtmlProps, selection = props.selection, _c = props.loading, loading = _c === void 0 ? false : _c, className = props.className, style = props.style, _d = props.border, border = _d === void 0 ? 'row' : _d, _e = props.zebra, zebra = _e === void 0 ? false : _e, _f = props.size, size = _f === void 0 ? 'default' : _f, empty = props.empty;
    utils_1.assert(function predict() {
        if (selection != null && recordIdAccessor == null)
            return false;
        return true;
    }, 'recordIdAccessor is required while using selection.');
    // columns 引用不能每次 rerender 都发生改变，不然会额外 render 一次直到不变为止
    var columns = react_1.useMemo(function () {
        var _a;
        var rawColumns = (_a = props.columns) !== null && _a !== void 0 ? _a : utils_2.children2ColumnOptions(props.children);
        return rawColumns.map(column_options_1.createRtColumn);
    }, 
    // 内部 rerender 不会导致 children 引用变化
    [props.columns, props.children]);
    var prefix = ConfigProvider_1.usePrefix('table');
    var wrapPrefix = utils_1.concat(prefix, 'wrap');
    var headerPrefix = utils_1.concat(prefix, 'header');
    var bodyPrefix = utils_1.concat(prefix, 'body');
    // undefined 表示没有开启行选择
    var selectionType = selection ? selection.type || 'multiple' : undefined;
    var _g = react_table_1.useTable({
        columns: columns,
        data: records,
        autoResetPage: false,
        autoResetSelectedRows: false,
        defaultCanFilter: true,
        getRowId: react_1.useCallback(function (row, relativeIndex) { var _a; return (_a = utils_2.getRowId(row, recordIdAccessor)) !== null && _a !== void 0 ? _a : String(relativeIndex); }, [recordIdAccessor]),
        initialState: {
            selectedRowIds: utils_2.getSelectedRowIds((_a = selection === null || selection === void 0 ? void 0 : selection.selectedIds) !== null && _a !== void 0 ? _a : [])
        },
        // 处理 react-table 内部状态的地方，如果后面太复杂考虑抽出来
        useControlledState: function (state) {
            var selectedRowIds = state.selectedRowIds, restState = tslib_1.__rest(state, ["selectedRowIds"]);
            hooks_2.useUpdate(function () {
                var _a, _b, _c;
                // ids 不相同则通知外部
                if (!utils_2.areIdsEquals(selectedRowIds, utils_2.getSelectedRowIds((_a = selection === null || selection === void 0 ? void 0 : selection.selectedIds) !== null && _a !== void 0 ? _a : []))) {
                    var nextSelectedIds = Object.keys(selectedRowIds).filter(function (id) { return selectedRowIds[id]; });
                    if (selectionType === 'single') {
                        (_b = selection === null || selection === void 0 ? void 0 : selection.onChange) === null || _b === void 0 ? void 0 : _b.call(selection, difference_1.default(nextSelectedIds, selection === null || selection === void 0 ? void 0 : selection.selectedIds));
                        return;
                    }
                    (_c = selection === null || selection === void 0 ? void 0 : selection.onChange) === null || _c === void 0 ? void 0 : _c.call(selection, nextSelectedIds);
                }
            }, [selectedRowIds]);
            return tslib_1.__assign({ 
                // 受控 selection 实现方式：被 getToggleRowSelectedProps.onChange 修改为选择状态
                // 经过这边处理，拦截一遍，需要把 table 内部 state 还原
                selectedRowIds: react_1.useMemo(
                // eslint-disable-next-line react-hooks/exhaustive-deps
                function () { var _a; return utils_2.getSelectedRowIds((_a = selection === null || selection === void 0 ? void 0 : selection.selectedIds) !== null && _a !== void 0 ? _a : []); }, [selection === null || selection === void 0 ? void 0 : selection.selectedIds]) }, restState);
        }
    }, react_table_1.useFilters, react_table_1.useSortBy, react_table_1.useRowSelect, react_table_1.useRowState, selection && withSelectionColumn_1.default || (function () { return null; })), getTableProps = _g.getTableProps, getTableBodyProps = _g.getTableBodyProps, headerGroups = _g.headerGroups, rows = _g.rows, prepareRow = _g.prepareRow;
    var tableContextValue = react_1.useMemo(function () { return ({
        size: 'default',
        prefix: prefix,
        selectionType: selectionType
    }); }, [prefix, selectionType]);
    var tableClassName = classnames_1.default(prefix, tableHtmlProps === null || tableHtmlProps === void 0 ? void 0 : tableHtmlProps.className, utils_1.concat(prefix, 'border', zebra ? 'none' : border), zebra && utils_1.concat(prefix, 'zebra'), utils_1.concat(prefix, 'size', size));
    return (react_1.default.createElement(hooks_1.tableContext.Provider, { value: tableContextValue },
        react_1.default.createElement("div", { className: classnames_1.default(wrapPrefix, className), style: style },
            react_1.default.createElement(Loading_1.Loading, { loading: loading },
                react_1.default.createElement("table", tslib_1.__assign({}, tableHtmlProps, getTableProps({
                    className: tableClassName,
                    style: tableHtmlProps === null || tableHtmlProps === void 0 ? void 0 : tableHtmlProps.style
                })),
                    react_1.default.createElement("thead", { className: headerPrefix }, headerGroups.map(function (headerGroup) { return (
                    // eslint-disable-next-line react/jsx-key
                    react_1.default.createElement("tr", tslib_1.__assign({}, headerGroup.getHeaderGroupProps({ className: utils_1.concat(headerPrefix, 'tr') })), headerGroup.headers.map(function (column) { return column.render('Header', { key: column.id }); }))); })),
                    react_1.default.createElement("tbody", tslib_1.__assign({ className: bodyPrefix }, getTableBodyProps()),
                        rows.map(function (row) {
                            prepareRow(row);
                            return (
                            // eslint-disable-next-line react/jsx-key
                            react_1.default.createElement("tr", tslib_1.__assign({}, row.getRowProps({ className: utils_1.concat(bodyPrefix, 'tr') })), row.cells.map(function (cell) { return cell.render('Cell', { key: cell.column.id }); })));
                        }),
                        records.length === 0 && react_1.default.createElement(Placeholder_1.default, { colSpan: columns.length, empty: empty }))),
                pagination && rows.length > 0 && react_1.default.createElement(Pagination_1.default, tslib_1.__assign({}, pagination))))));
}
exports.default = Table;
function TableColumn() {
    return null;
}
exports.TableColumn = TableColumn;
Table.Column = TableColumn;
//# sourceMappingURL=Table.js.map